import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { ConditionalLeaderboard, ConditionalQuestionDisplay, WaitingSpinner, AnswerFeedbackCard, QuestionFeedbackSection, TimerBar, GameEndedScreen, HostPlayingScoreStrip } from '../shared';
import { useHostPlaying } from '../../hooks/useHostPlaying';
import { calculateTimeRemaining } from '../../utils/gameUtils';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import '../../App.css';
import { useTranslation } from '../../i18n';

interface SpeedChallengeQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
  challengeType: 'type-word' | 'emoji-identify' | 'math' | 'reverse' | 'color-sequence' | 'pattern' | 'shape';
  displayContent: string;
  displayColors?: string[];
  isSpeedChallenge?: boolean;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  rankedPlayers?: Array<{
    playerId: string;
    playerName: string;
    rank: number;
  }>;
}

interface SpeedChallengePlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: any; // Accept generic question type, speed-challenge fields may be present
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

const SpeedChallengePlayerDisplay: React.FC<SpeedChallengePlayerDisplayProps> = ({ 
  roomState, 
  socket, 
  myPlayerId,
  onGoHome,
  isHost = false,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const hostIsPlaying = useHostPlaying(roomState);
  const [currentQuestion, setCurrentQuestion] = useState<SpeedChallengeQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<{
    mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
    wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
    totalQuestions: number;
    averageScore: number;
  } | null>(null);
  const [answer, setAnswer] = useState('');
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [rankPhrase, setRankPhrase] = useState<{ emoji: string; text: string; color: string } | null>(null);
  const [myScore, setMyScore] = useState(0);
  const [showInterestingFact, setShowInterestingFact] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState<string | null>(null);
  const [acceptedAnswers, setAcceptedAnswers] = useState<string[] | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef<boolean>(false);
  const answerSubmittedTimeRef = useRef<number | null>(null);
  const previousQuestionIdRef = useRef<string | undefined>(
    roomState.currentQuestion?.questionId || roomState.currentQuestion?.id || undefined
  );
  const currentQuestionIdRef = useRef<string | undefined>(
    roomState.currentQuestion?.questionId || roomState.currentQuestion?.id || undefined
  );
  const gameEndData = useGameEndScreenData(roomState);
  const roomQuestion = roomState.currentQuestion;
  const roomQuestionStartTime = (roomState as any).questionStartTime;

  // Helper to reset question state
  const resetQuestion = () => {
    setAnswer('');
    setAnswerSubmitted(false);
    setAnswerCorrect(null);
    setPointsEarned(0);
    setRankPhrase(null);
    setShowInterestingFact(false);
    setCorrectAnswer(null);
    setAcceptedAnswers(null);
    isSubmittingRef.current = false;
    answerSubmittedTimeRef.current = null;
    // Focus input for quick typing
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // When server says game is finished (e.g. reconnection, or room-state before game-ended), show game end screen
  const roomGameState = roomState.gameState;
  useEffect(() => {
    if (roomGameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- gameEndData is derived from roomState
  }, [roomGameState, gameEndData]);

  // Sync score from roomState
  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const myPlayer = roomState.players.find(p => p.id === myPlayerId);
      if (myPlayer) {
        setMyScore(myPlayer.score);
      }
    }
  }, [roomState.players, myPlayerId]);

  // Update when roomState.currentQuestion changes
  useEffect(() => {
    if (roomQuestion) {
      const newQuestionId = roomQuestion.questionId || roomQuestion.id;
      const isNewQuestion = newQuestionId !== previousQuestionIdRef.current;
      const serverTimeRemaining = calculateTimeRemaining(roomQuestion, roomQuestionStartTime);
      if (isNewQuestion || !currentQuestion) {
        previousQuestionIdRef.current = newQuestionId;
        currentQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(roomQuestion);
        setTimeRemaining(serverTimeRemaining);
        if (isNewQuestion) {
          resetQuestion();
        }
      } else {
        setTimeRemaining(serverTimeRemaining);
      }
    }
  }, [roomQuestion, roomQuestionStartTime, currentQuestion]);

  useEffect(() => {
    const handleQuestion = (data: SpeedChallengeQuestion) => {
      console.log('SpeedChallengePlayerDisplay received question:', data);
      const newQuestionId = data.questionId || data.id;
      
      if (newQuestionId !== previousQuestionIdRef.current) {
        previousQuestionIdRef.current = newQuestionId;
        currentQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(data);
        setTimeRemaining((data as any).timeRemaining ?? data.timeLimit);
        resetQuestion();
      }
    };

    const handleQuestionResult = (data: QuestionResult) => {
      console.log('Question result:', data);
      
      // If current player was the last one to answer, show flip immediately
      if (answerSubmittedTimeRef.current) {
        const timeSinceSubmit = Date.now() - answerSubmittedTimeRef.current;
        // If less than 1.5 seconds since submission, they were the last player - flip immediately
        if (timeSinceSubmit < 1500) {
          setTimeout(() => {
            setShowInterestingFact(true);
          }, 500); // Quick 500ms delay for last player
        }
      }
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: {
        mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
        wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
        totalQuestions: number;
        averageScore: number;
      };
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      // Don't set currentQuestion to null here - let the question event set the actual question
      // This avoids a race condition where the UI gets stuck on "Loading" until user interaction
      resetQuestion();
    };

    const handleAnswerReceived = (data: { 
      isCorrect: boolean; 
      points: number; 
      responseTime: number; 
      questionId?: string;
      rank?: number | null;
      correctAnswer?: string;
      acceptedAnswers?: string[];
    }) => {
      const eventQuestionId = data.questionId ?? currentQuestionIdRef.current;
      if (currentQuestionIdRef.current && eventQuestionId !== currentQuestionIdRef.current) {
        return;
      }
      console.log('Answer received:', data);
      isSubmittingRef.current = false;
      setAnswerSubmitted(true);
      setAnswerCorrect(data.isCorrect);
      setPointsEarned(data.points);
      // Score is synced from room-state; don't add data.points to avoid wrong + points when events reorder
      if (data.rank) {
        // Generate and store the phrase immediately when rank is set
        const pickRandom = (arr: string[]): string => arr[Math.floor(Math.random() * arr.length)];
        const rankColors: Record<number, string> = {
          1: '#FACC15', 2: '#CBD5E1', 3: '#F59E0B', 4: '#34D399', 5: '#34D399',
        };
        const rankEmojis: Record<number, string> = {
          1: '🥇', 2: '🥈', 3: '🥉', 4: '⚡', 5: '👍',
        };
        const rankKey = data.rank >= 1 && data.rank <= 5 ? String(data.rank) : 'default';
        const texts = t(`gameplay.speedChallenge.rankFeedback.${rankKey}`, { returnObjects: true }) as string[];
        const emoji = rankEmojis[data.rank] ?? '✓';
        const color = rankColors[data.rank] ?? '#34D399';
        setRankPhrase({ emoji, text: pickRandom(texts), color });
      }
      // Store correct answer info for wrong answers
      if (!data.isCorrect && data.correctAnswer) {
        setCorrectAnswer(data.correctAnswer);
        setAcceptedAnswers(data.acceptedAnswers || null);
      }

      // Track submission time for last-player detection
      answerSubmittedTimeRef.current = Date.now();
      
      // Show interesting fact after a short delay
      // Store the question ID at the time of answering to guard the timeout
      const answeredQuestionId = currentQuestionIdRef.current;
      setShowInterestingFact(false);
      setTimeout(() => {
        // Only show the flip if we're still on the same question
        if (currentQuestionIdRef.current === answeredQuestionId) {
          setShowInterestingFact(true);
        }
      }, 1500);
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);
    socket.on('answer-received', handleAnswerReceived);

    return () => {
      // Pass specific handler to only remove THIS component's listener, not App.tsx's
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
      socket.off('answer-received', handleAnswerReceived);
    };
  }, [socket, myPlayerId]);

  // Timer countdown
  useEffect(() => {
    if (currentQuestion && timeRemaining > 0 && !answerSubmitted) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 100) return 0;
          return prev - 100;
        });
      }, 100);

      return () => clearInterval(timer);
    }
  }, [currentQuestion, timeRemaining, answerSubmitted]);

  const handleSubmitAnswer = () => {
    if (!answer.trim() || !currentQuestion || answerSubmitted || isSubmittingRef.current) {
      return;
    }

    const questionId = currentQuestion.questionId || currentQuestion.id;
    if (!questionId) {
      console.error('Question ID not found');
      return;
    }

    isSubmittingRef.current = true;
    setAnswerSubmitted(true);

    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: questionId,
      answer: answer.trim().toUpperCase(),
      timestamp: Date.now(),
    });
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitAnswer();
    }
  };

  // Get challenge type hint - uses the actual question for pattern/shape types
  const getChallengeHint = (type: string, question?: string): string => {
    // For pattern and shape types, use the actual question as it's more specific
    if ((type === 'pattern' || type === 'shape') && question) {
      return question;
    }
    const hints: Record<string, string> = {
      'type-word': t('gameplay.speedChallenge.hints.typeWord'),
      'emoji-identify': t('gameplay.speedChallenge.hints.emojiIdentify'),
      'math': t('gameplay.speedChallenge.hints.math'),
      'reverse': t('gameplay.speedChallenge.hints.reverse'),
      'color-sequence': t('gameplay.speedChallenge.hints.colorSequence'),
      'pattern': t('gameplay.speedChallenge.hints.pattern'),
      'shape': t('gameplay.speedChallenge.hints.shape'),
    };
    return hints[type] || t('gameplay.speedChallenge.hints.default');
  };

  // Game ended screen
  if (gameEnded) {
    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        resetQuestion();
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    const displayLeaderboard = finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? []);
    const displayStatistics = gameStatistics ?? gameEndData?.gameStatistics ?? null;

    return (
      <GameEndedScreen
        gameIcon="⚡"
        finalLeaderboard={displayLeaderboard}
        myPlayerId={myPlayerId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        showYouSuffix
        statistics={displayStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  // Loading state
  if (!currentQuestion) {
    return (
      <div className="app">
        <div className="container">
          <h1 className="title">{t('gameplay.speedChallenge.title')}</h1>
          <p className="subtitle">{t('gameplay.speedChallenge.getReady')}</p>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid rgba(248, 250, 252, 0.3)',
              borderTop: '4px solid #F8FAFC',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    }
  };

  return (
    <div className="app">
      <Header
        leftButton={{
          icon: HeaderIcons.home,
          onClick: handleGoHome,
          tooltip: t('gameplay.leaveGame')
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage')
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <span className="header-game-name" style={{ fontSize: '1rem' }}>{t('gameplay.speedChallenge.title')}</span>
            <span style={{ 
              fontSize: '1.2rem', 
              fontWeight: 'bold', 
              color: '#34D399',
              padding: '0.3rem 0.6rem',
              backgroundColor: 'rgba(52, 211, 153, 0.1)',
              borderRadius: '6px',
              border: '2px solid #34D399',
            }}>
              {roomState.roomName}
            </span>
          </div>
        }
      />
      <div className="container player-content-wrapper" style={{ paddingTop: '3rem', width: '100%', maxWidth: '100%' }}>
        <HostPlayingScoreStrip
          hostIsPlaying={hostIsPlaying}
          players={roomState.players}
          myPlayerId={myPlayerId}
          pointsThisQuestion={answerSubmitted ? pointsEarned : null}
          showQuestionPointsLine={answerSubmitted}
        />
        {!answerSubmitted ? (
          <>
            <ConditionalLeaderboard
              hostIsPlaying={hostIsPlaying}
              players={roomState.players}
              myPlayerId={myPlayerId}
            />
            <TimerBar
              timeRemaining={timeRemaining}
              timeLimit={currentQuestion.timeLimit}
              questionNumber={currentQuestion.questionNumber}
              totalQuestions={currentQuestion.totalQuestions}
              showTimeCounter={false}
            />
            <ConditionalQuestionDisplay
              hostIsPlaying={hostIsPlaying}
              question={currentQuestion.question}
              displayContent={currentQuestion.displayContent}
              challengeType={currentQuestion.challengeType}
            />
            <div style={{ marginBottom: '1.5rem' }}>
              <style>{`
                .speed-input::placeholder {
                  font-size: 0.9rem;
                  letter-spacing: 0;
                  text-transform: none;
                }
              `}</style>
              <input
                ref={inputRef}
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder={getChallengeHint(currentQuestion.challengeType, currentQuestion.question)}
                autoComplete="off"
                autoCapitalize="characters"
                className="speed-input"
                style={{
                  width: '100%',
                  padding: '1.2rem',
                  fontSize: '1.5rem',
                  fontFamily: 'monospace',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.08) 0%, rgba(var(--color-secondary-rgb), 0.14) 100%)',
                  border: '3px solid rgba(var(--color-secondary-rgb), 0.28)',
                  borderRadius: '15px',
                  color: 'var(--text-primary)',
                  outline: 'none',
                }}
              />
            </div>
            <button
              onClick={handleSubmitAnswer}
              disabled={!answer.trim()}
              className="button player-answer-submit-button"
              style={{
                width: '100%',
                backgroundColor: answer.trim() ? '#34D399' : undefined,
              }}
            >
              {t('gameplay.submitAnswer')}
            </button>
          </>
        ) : (
          <div style={{
            width: '100%',
            minHeight: 'calc(100dvh - 200px)',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
          }}>
            <ConditionalLeaderboard
              hostIsPlaying={hostIsPlaying}
              players={roomState.players}
              myPlayerId={myPlayerId}
            />
            {answerCorrect !== null && (
              <AnswerFeedbackCard
                isCorrect={answerCorrect}
                showDidYouKnow={showInterestingFact}
                interestingFact={currentQuestion?.interesting}
                rankText={rankPhrase?.text}
                pointsEarned={pointsEarned}
                correctAnswer={correctAnswer || undefined}
                acceptedAnswers={acceptedAnswers || undefined}
              />
            )}
            <WaitingSpinner isLoading={false} />
            {answerCorrect !== null && currentQuestion && (
              <div style={{ marginTop: 'auto', width: '100%' }}>
                <QuestionFeedbackSection
                  questionId={currentQuestion.questionId || currentQuestion.id || ''}
                  roomName={roomState.roomName}
                  gameType={roomState.gameType}
                  answerSubmitted={answerSubmitted}
                  showFeedback={true}
                  onSubmitFeedback={(rating, correct, questionText, correctAnswerVal) => {
                    if ((rating !== undefined || correct !== undefined) && socket) {
                      socket.emit('question-feedback', {
                        roomName: roomState.roomName,
                        questionId: currentQuestion.questionId || currentQuestion.id,
                        gameType: roomState.gameType,
                        rating: rating ?? undefined,
                        correct: correct ?? undefined,
                        questionText: questionText ?? currentQuestion.question,
                        correctAnswer: correctAnswerVal ?? correctAnswer ?? undefined,
                      });
                    }
                  }}
                  questionText={currentQuestion.question}
                  correctAnswer={correctAnswer ?? undefined}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeedChallengePlayerDisplay;

